import React, { useState, useRef, useEffect } from "react";
import { 
  Paper, Typography, Box, Button, CircularProgress, TextareaAutosize
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm'; // Add this import for table support

function ChatBox({ threadId, vectorStoreId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [files, setFiles] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(true);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load chat history on component mount
  useEffect(() => {
    const loadChatHistory = async () => {
      if (!threadId) return;
      
      try {
        setLoading(true);
        var url = `${process.env.REACT_APP_API_URL}/chat/${threadId}/history`;
        const response = await fetch(
          url,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          
          const formattedMessages = data.history.map(msg => ({
            role: msg.role === "model" ? "assistant" : msg.role,
            text: msg.content,
            files: msg.files || []
          }));
          
          setMessages(formattedMessages);
        } else if (response.status === 404) {
          setMessages([]);
        } else {
          console.error("Failed to load chat history:", response.statusText);
          setMessages([]);
        }
      } catch (error) {
        console.error("Error loading chat history:", error);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    loadChatHistory();
  }, [threadId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFileChange = (e) => {
    setFiles(e.target.files);
  };

  const handleUploadFiles = async () => {
    if (!files || files.length === 0) return;
    
    setStreaming(true);
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append("files", file));
    
    try {
      const response = await fetch(`/upload/${threadId}`, {
        method: "POST", 
        body: formData
      });
      
      if (response.ok) {
        setFiles(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setStreaming(false);
    }
  };

  // Handle form submission and Enter key behavior
  const handleFormSubmit = (e) => {
    e.preventDefault();
    sendMessage(e);
  };

  // Handle Enter key in textarea (Shift+Enter = new line, Enter = send)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const sendMessage = async (e, customMessage = null) => {
    e.preventDefault();
    const messageToSend = customMessage || input.trim();
    
    if (streaming || (!messageToSend && (!files || files.length === 0))) return;

    setStreaming(true);
    
    if (messageToSend) {
      setMessages(prev => [...prev, { role: "user", text: messageToSend }]);
    }
    
    const formData = new FormData();
    formData.append("thread_id", threadId);
    formData.append("message", messageToSend);
    if (vectorStoreId) formData.append("vector_store_id", vectorStoreId);

    if (files) {
      Array.from(files).forEach(file => formData.append("files", file));
    }

    try {
      var url = `${process.env.REACT_APP_API_URL}/chat/`;

      const response = await fetch(url, {
        credentials: 'include',
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        if (!customMessage) {
          setInput("");
        }
        setFiles(null);
        if (fileInputRef.current) fileInputRef.current.value = "";

        const contentType = response.headers.get("content-type");
        if (contentType?.includes("text/event-stream")) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let assistantMessage = "";

          setMessages(prev => [...prev, { role: "assistant", text: "" }]);

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') {
                  console.log('Final markdown text:', JSON.stringify(assistantMessage));
                  console.log('Final markdown text (formatted):', assistantMessage);
                  setStreaming(false);
                  return;
                }
                
                // Debug: Log what we're receiving
                console.log('Received line:', JSON.stringify(data));
                
                assistantMessage += data + "\n";
                
                setMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1] = {
                    role: "assistant", 
                    text: assistantMessage
                  };
                  return newMessages;
                });
              }
            }
          }
        } else {
          const result = await response.json();
          if (result.text) {
            setMessages(prev => [...prev, { role: "assistant", text: result.text }]);
          }
        }
      } else {
        console.error("Chat error:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setStreaming(false);
    }
  };

  const handleReassessment = async () => {
    const syntheticEvent = { preventDefault: () => {} };
    await sendMessage(syntheticEvent, "reassess");
  };

  // Show loading spinner while loading history
  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 2, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading chat history...</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 1, sm: 3 }, mt: 2, boxShadow: 3, width: "100%", maxWidth: 600, mx: "auto" }}>
      <Typography variant="h6" gutterBottom>Chat with POPPi</Typography>
      <Box sx={{ minHeight: 200, maxHeight: 300, overflowY: "auto", mb: 2, width: "100%" }}>
        {messages.map((msg, idx) => (
          <Box key={idx} sx={{ color: msg.role === "user" ? "#333" : "#0074D9", mb: 1, wordBreak: "break-word" }}>
            <b>{msg.role === "user" ? "You" : "POPPi's AI"}:</b>{" "}
            {msg.role === "assistant" ? (
              msg.html ? (
                <Paper sx={{ mt: 3, p: 2, background: "#f9f9f9" }}>
                  <div dangerouslySetInnerHTML={{ __html: msg.html }} />
                </Paper>
              ) : (
                <Box sx={{ 
                  '& table': { 
                    borderCollapse: 'collapse', 
                    width: '100%',
                    mt: 1,
                    mb: 1,
                    border: '1px solid #ddd'
                  },
                  '& th, & td': { 
                    border: '1px solid #ddd', 
                    padding: '8px', 
                    textAlign: 'left' 
                  },
                  '& th': { 
                    backgroundColor: '#f2f2f2',
                    fontWeight: 'bold'
                  },
                  '& tr:nth-of-type(even)': {
                    backgroundColor: '#f9f9f9'
                  }
                }}>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({node, ...props}) => (
                        <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props} />
                      ),
                      th: ({node, ...props}) => (
                        <th style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f2f2f2' }} {...props} />
                      ),
                      td: ({node, ...props}) => (
                        <td style={{ border: '1px solid #ddd', padding: '8px' }} {...props} />
                      )
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>
                </Box>
              )
            ) : (
              msg.text
            )}
          </Box>
        ))}
        <div ref={chatEndRef} />
      </Box>
      
      <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, width: "100%", gap: 1 }}>
          <TextareaAutosize
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={streaming}
            placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
            minRows={2}
            maxRows={6}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              fontFamily: 'inherit',
              border: '1px solid #ccc',
              borderRadius: '4px',
              resize: 'none',
              outline: 'none',
              backgroundColor: streaming ? '#f5f5f5' : 'white',
              color: streaming ? '#999' : 'inherit'
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={streaming || (!input.trim() && (!files || files.length === 0))}
            sx={{ minWidth: 100, alignSelf: 'flex-end' }}
          >
            {streaming ? <CircularProgress size={20} /> : "Send"}
          </Button>
        </Box>
        
        {/* Button row for Upload Files and Perform Re-Assessment */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, width: "100%" }}>
          <Button variant="outlined" component="label" sx={{ flex: 1 }}>
            Upload Files
            <input
              type="file"
              multiple
              hidden
              onChange={handleFileChange}
              disabled={streaming}
              ref={fileInputRef}
            />
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReassessment}
            disabled={streaming}
            sx={{ flex: 1 }}
          >
            Perform Re-Assessment
          </Button>
        </Box>
        
        {/* Show selected files */}
        {files && files.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Files to be sent:</Typography>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {Array.from(files).map((file, idx) => (
                <li key={idx} style={{ fontSize: 13 }}>{file.name}</li>
              ))}
            </ul>
          </Box>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleUploadFiles}
          disabled={streaming || !files || files.length === 0}
          sx={{ mt: 1 }}
        >
          Upload
        </Button>
      </form>
    </Paper>
  );
}

export default ChatBox;